/**
 * AdminDashboard — V2 reskin
 * Same API calls / data flow. Presentation updated to:
 *   - 4 KPI tiles (.kpis grid)
 *   - CSS conic-gradient check-in donut
 *   - Zone activity list with bar fill
 *   - Quick actions 2×2
 *   - Recent activity feed (real scan events)
 *   - Revenue ledger card (only if paid event)
 */

import { useState, useEffect } from 'react';
import {
  UserCheck, Ticket, IndianRupee, Zap, ScanLine, UserPlus,
  Activity, Download, DoorOpen, Utensils, MapPin, BarChart3,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { eventsApi, walletApi } from '../api/httpClient';
import { useAnimatedNumber } from '../hooks/useAnimatedNumber';

// ── KPI tile ──────────────────────────────────────────────────────────────────
function KpiTile({ val, label, Icon, bgColor, iconColor, badge }) {
  const animated = useAnimatedNumber(val || 0, 1000);
  return (
    <div className="card kpi">
      <div className="kpi-ico" style={{ background: bgColor, color: iconColor }}>
        <Icon />
      </div>
      <div className="kpi-val">{animated.toLocaleString()}</div>
      <div className="kpi-label">
        {label}
        {badge && (
          <span style={{
            marginLeft: 6, fontSize: 11, fontWeight: 700, padding: '2px 8px',
            borderRadius: 99, background: 'var(--success-bg)', color: 'var(--success-ink)',
          }}>{badge}</span>
        )}
      </div>
    </div>
  );
}

// ── CSS conic-gradient donut ──────────────────────────────────────────────────
function CheckInDonut({ checkins, registered, capacity }) {
  const pct = registered > 0 ? Math.round((checkins / registered) * 100) : 0;
  const notArrived = registered - checkins;
  const remaining  = capacity ? Math.max(0, capacity - registered) : 0;

  return (
    <div className="card panel">
      <div className="panel-head">
        Check-in progress
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 99,
          background: 'var(--success-bg)', color: 'var(--success-ink)',
          animation: 'livePulse 1.6s ease-in-out infinite',
        }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--success)', display: 'inline-block' }} />
          Updating live
        </span>
      </div>

      <div className="donut-row">
        {/* CSS conic-gradient donut */}
        <div className="donut" style={{
          background: `conic-gradient(var(--primary) ${pct}%, var(--bg-2,#EDE8F4) 0)`,
        }}>
          <div className="mid">
            <div className="big">{pct}%</div>
            <div className="sub">{checkins.toLocaleString()} / {registered.toLocaleString()}</div>
          </div>
        </div>

        <div className="breakdown">
          <div className="brk">
            <span className="sw" style={{ background: 'var(--primary)' }} />
            <span className="nm">Checked in</span>
            <span className="vl">{checkins.toLocaleString()}</span>
            <span className="pc">{pct}%</span>
          </div>
          <div className="brk">
            <span className="sw" style={{ background: 'var(--warning)' }} />
            <span className="nm">Registered · not arrived</span>
            <span className="vl">{notArrived.toLocaleString()}</span>
            <span className="pc">{100 - pct}%</span>
          </div>
          {capacity > 0 && (
            <div className="brk">
              <span className="sw" style={{ background: 'var(--bg-2,#EDE8F4)' }} />
              <span className="nm">Seats remaining</span>
              <span className="vl">{remaining.toLocaleString()}</span>
              <span className="pc">—</span>
            </div>
          )}
          <hr style={{ border: 'none', borderTop: '1px solid var(--line)', margin: '4px 0' }} />
          <div className="brk">
            <span className="nm" style={{ color: 'var(--ink-2)' }}>No-show rate</span>
            <span className="vl" style={{ color: registered > 0 && (100 - pct) > 30 ? 'var(--danger-ink)' : 'var(--ink)' }}>
              {100 - pct}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Zone activity list ────────────────────────────────────────────────────────
const ZONE_COLORS = [
  { bg: 'var(--primary-50)', color: 'var(--primary-700)', Icon: DoorOpen   },
  { bg: 'var(--violet-50)',  color: 'var(--violet)',       Icon: Utensils   },
  { bg: 'var(--success-bg)', color: 'var(--success)',      Icon: Activity   },
  { bg: 'var(--warning-bg)', color: 'var(--warning)',      Icon: MapPin     },
];

function ZoneActivity({ chartData, onManage }) {
  if (!chartData.length) return null;

  const maxCount = Math.max(...chartData.map(d => (d.success || 0) + (d.already_scanned || 0)));

  return (
    <div className="card panel">
      <div className="panel-head">
        Zone activity
        <span className="lk" onClick={onManage}>Manage zones</span>
      </div>

      {chartData.slice(0, 6).map((zone, i) => {
        const total  = (zone.success || 0) + (zone.already_scanned || 0);
        const fillPct = maxCount > 0 ? Math.round((total / maxCount) * 100) : 0;
        const meta   = ZONE_COLORS[i % ZONE_COLORS.length];
        const { Icon } = meta;

        return (
          <div key={zone.zone} className="zone-row">
            <div className="zone-ico" style={{ background: meta.bg, color: meta.color }}>
              <Icon size={18} strokeWidth={1.75} />
            </div>
            <div style={{ minWidth: 100 }}>
              <div style={{ fontWeight: 700, fontSize: 15 }}>{zone.zone}</div>
              <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>
                {zone.denied ? `${zone.denied} denied` : 'Zone'}
              </div>
            </div>
            <div className="zone-bar">
              <div className="b"><div className="f" style={{ width: `${fillPct}%` }} /></div>
            </div>
            <div className="zone-val">{total.toLocaleString()}</div>
          </div>
        );
      })}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function AdminDashboard({ eventId }) {
  const navigate = useNavigate();
  const params   = useParams();
  const id       = eventId || Number(params.id);

  const [summary, setSummary] = useState(null);
  const [stats, setStats]     = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    Promise.all([
      eventsApi.summary(id)           .catch(() => null),
      eventsApi.scanStats(id)          .catch(() => ({ stats: [] })),
      walletApi.balance()              .catch(() => null),
      eventsApi.revenue(id)            .catch(() => null),
    ]).then(([s, st, , rev]) => {
      setSummary(s);
      setStats(st?.stats || []);
      setRevenue(rev);
    }).finally(() => setLoading(false));

    // Live refresh every 30s
    const t = setInterval(() => {
      eventsApi.summary(id).then(s => s && setSummary(s)).catch(() => {});
      eventsApi.scanStats(id).then(d => setStats(d?.stats || [])).catch(() => {});
    }, 30000);
    return () => clearInterval(t);
  }, [id]);

  // Transform stats into zone chartData
  const chartData = stats.reduce((acc, row) => {
    const existing = acc.find(r => r.zone === row.zone_name);
    if (existing) { existing[row.result] = row.count; }
    else { acc.push({ zone: row.zone_name, [row.result]: row.count }); }
    return acc;
  }, []);

  const checkins    = summary?.total_checkins   || 0;
  const registered  = summary?.total_registered || 0;
  const capacity    = summary?.capacity         || 0;
  const totalRevenue = revenue?.total_revenue   || 0;

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
      <div style={{ width: 40, height: 40, border: '4px solid var(--line)', borderTopColor: 'var(--primary)', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } } @keyframes livePulse { 0%,100%{opacity:1} 50%{opacity:0.35} }`}</style>
    </div>
  );

  return (
    <div className="page">
      <style>{`@keyframes spin{to{transform:rotate(360deg)}} @keyframes livePulse{0%,100%{opacity:1}50%{opacity:0.35}}`}</style>

      {/* ── KPI tiles ───────────────────────────────── */}
      <div className="kpis">
        <KpiTile
          Icon={UserCheck} val={checkins} label="Checked in"
          bgColor="var(--primary-50)" iconColor="var(--primary-700)"
          badge={registered > 0 ? `${Math.round((checkins / registered) * 100)}%` : null}
        />
        <KpiTile
          Icon={Ticket} val={registered}
          label={capacity ? `Registered of ${capacity.toLocaleString()}` : 'Registered'}
          bgColor="var(--violet-50)" iconColor="var(--violet)"
        />
        <KpiTile
          Icon={IndianRupee} val={Math.round(totalRevenue)}
          label="Ticket revenue"
          bgColor="var(--success-bg)" iconColor="var(--success)"
        />
        <KpiTile
          Icon={Zap} val={summary?.total_zones || 0}
          label="Active zones"
          bgColor="var(--warning-bg)" iconColor="var(--warning)"
        />
      </div>

      {/* ── Main 2-col grid ─────────────────────────── */}
      <div className="dash-grid">

        {/* Left: donut + zone activity */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <CheckInDonut checkins={checkins} registered={registered} capacity={capacity} />
          {chartData.length > 0 && (
            <ZoneActivity chartData={chartData} onManage={() => navigate(`/events/${id}/configure`)} />
          )}
        </div>

        {/* Right: quick actions + revenue */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

          {/* Quick actions */}
          <div className="card panel">
            <div className="panel-head">Quick actions</div>
            <div className="quick">
              <a className="qa" href={`/scan/event/${id}`}>
                <ScanLine size={18} strokeWidth={1.75} /> Open scanner
              </a>
              <a className="qa" onClick={e => { e.preventDefault(); navigate(`/events/${id}/attendees`); }} href="#">
                <UserPlus size={18} strokeWidth={1.75} /> Attendees
              </a>
              <a className="qa" onClick={e => { e.preventDefault(); navigate(`/events/${id}/live`); }} href="#">
                <Activity size={18} strokeWidth={1.75} /> Live ops
              </a>
              <a className="qa" onClick={e => { e.preventDefault(); navigate(`/events/${id}/analytics`); }} href="#">
                <Download size={18} strokeWidth={1.75} /> Analytics
              </a>
            </div>
          </div>

          {/* Revenue — only if paid */}
          {revenue && (revenue.paid_count > 0) && (
            <div className="card panel">
              <div className="panel-head">
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <IndianRupee size={16} strokeWidth={2} /> Revenue
                </span>
                <span className="lk" onClick={() => navigate(`/events/${id}/analytics`)}>Full report</span>
              </div>

              {/* Summary tiles */}
              <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, background: 'var(--success-bg)', borderRadius: 'var(--r-md)', padding: '10px 14px' }}>
                  <div style={{ fontSize: 11, color: 'var(--ink-2)', fontWeight: 600, textTransform: 'uppercase' }}>Total</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--success-ink)' }}>
                    ₹{Number(totalRevenue).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                  </div>
                </div>
                <div style={{ flex: 1, background: 'var(--violet-50)', borderRadius: 'var(--r-md)', padding: '10px 14px' }}>
                  <div style={{ fontSize: 11, color: 'var(--ink-2)', fontWeight: 600, textTransform: 'uppercase' }}>Paid tickets</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--violet)' }}>{revenue.paid_count}</div>
                </div>
              </div>

              {/* Breakdown table */}
              {revenue.breakdown?.length > 0 && (
                <div className="tbl-scroll">
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid var(--line)' }}>
                        <th style={{ textAlign: 'left', padding: '7px 8px', fontWeight: 700 }}>Tier</th>
                        <th style={{ textAlign: 'right', padding: '7px 8px', fontWeight: 700 }}>Sold</th>
                        <th style={{ textAlign: 'right', padding: '7px 8px', fontWeight: 700 }}>Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {revenue.breakdown.map((row, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid var(--surface-2)' }}>
                          <td style={{ padding: '8px 8px', fontWeight: 600 }}>{row.tier_name}</td>
                          <td style={{ padding: '8px 8px', textAlign: 'right', color: 'var(--ink-2)' }}>{row.sold}</td>
                          <td style={{ padding: '8px 8px', textAlign: 'right', fontWeight: 700, color: 'var(--success-ink)' }}>
                            ₹{Number(row.revenue).toLocaleString('en-IN')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Scan activity summary (if no revenue) */}
          {(!revenue || revenue.paid_count === 0) && summary && (
            <div className="card panel">
              <div className="panel-head">Scan activity</div>
              <div className="act">
                <div className="act-ico" style={{ background: 'var(--success-bg)', color: 'var(--success)' }}>
                  <UserCheck size={16} strokeWidth={1.75} />
                </div>
                <div>
                  <div className="act-body"><strong>{checkins.toLocaleString()}</strong> total check-ins</div>
                  <div className="act-meta">Across all zones</div>
                </div>
              </div>
              {summary.total_denials > 0 && (
                <div className="act">
                  <div className="act-ico" style={{ background: 'var(--danger-bg,#FCEAEC)', color: 'var(--danger)' }}>
                    <BarChart3 size={16} strokeWidth={1.75} />
                  </div>
                  <div>
                    <div className="act-body"><strong>{summary.total_denials}</strong> denied scans</div>
                    <div className="act-meta">Duplicate or invalid QR</div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
